import { useEffect, useState } from 'react';
import { getEdmDashboardStats } from '../../../../helpers/api';
import { Box, Card, Grid2, Typography, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { 
  CorporateFare, 
  TrendingUp, 
  FactCheck, 
  AccountBalanceWallet 
} from '@mui/icons-material'; // Modern MUI Icons
import LoaderSkeleton from '../../../common/LoaderSkeleton';

// 1. REUSABLE STAT CARD (With support for the custom "otherText" Chip)
const StatCard = ({ title, value, subtitle, icon: IconComponent, color, type = '', otherText }) => {
  return (
    <Card
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'divider',
        background: 'linear-gradient(145deg, #ffffff, #fafbff)',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.03)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0px 10px 25px rgba(0, 0, 0, 0.08)',
          borderColor: alpha(color, 0.3),
        },
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="overline" color="textSecondary" sx={{ fontWeight: 600, letterSpacing: 1 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mt: 0.5 }}>
            {value !== undefined && value !== null ? value : 0}
            <Typography component="span" variant="h5" sx={{ ml: 0.5, color: 'text.secondary' }}>
              {type}
            </Typography>
          </Typography>
        </Box>

        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '14px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: alpha(color, 0.1),
            color: color,
          }}
        >
          <IconComponent fontSize="large" />
        </Box>
      </Box>

      <Box>
        <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500, mb: otherText ? 1 : 0 }}>
          {subtitle}
        </Typography>
        {/* Modern Tag for "In Progress" texts */}
        {otherText && (
          <Chip 
            label={otherText} 
            size="small" 
            sx={{ 
              bgcolor: alpha(color, 0.1), 
              color: color, 
              fontWeight: 700,
              borderRadius: '8px'
            }} 
          />
        )}
      </Box>
    </Card>
  );
};

export default function StatCardsForEdm({ stateId }) {
  // 2. STATE MANAGEMENT (Holding pure data, no UI components)
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchStats();
  }, [stateId]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await getEdmDashboardStats(stateId);
      const responseData = response?.result?.data;
      setStatsData(responseData);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to format big numbers
  const formatNum = (num) => (num ? Intl.NumberFormat('en-NG').format(num) : 0);

  // 3. RENDER LOGIC
  if (loading) {
    return (
      <Grid2 container spacing={3}>
        {[1, 2, 3, 4].map((item) => (
          <Grid2 key={item} size={{ lg: 3, md: 6, sm: 6, xs: 12 }}>
            <Box sx={{ p: 2, height: '100%', bgcolor: '#fff', borderRadius: '16px' }}>
              <LoaderSkeleton />
            </Box>
          </Grid2>
        ))}
      </Grid2>
    );
  }

  // 4. UI CONFIGURATION
  // We map the raw state data to the visual cards here, keeping the logic perfectly clean.
  const cardConfigs = [
    {
      title: 'Facilities',
      value: formatNum(statsData?.facilityCount),
      subtitle: 'Total No. of Facilities',
      icon: CorporateFare,
      color: '#1976d2', // Blue
      type: '',
      otherText: '',
    },
    {
      title: 'Active',
      value: formatNum(statsData?.activeFacilityPercentage),
      subtitle: 'Total No. of Active Facilities',
      icon: TrendingUp,
      color: '#ff4842', // Red (matching your previous color config)
      type: '%',
      otherText: '',
    },
    {
      title: 'QA/QC Completion',
      value: formatNum(statsData?.completedQaQcReportPercentage),
      subtitle: 'QA/QC completion ratio',
      icon: FactCheck,
      color: '#ff9800', // Warning Orange
      type: '%',
      otherText: `${Intl.NumberFormat('en-NG', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(statsData?.facilityReportCount || 0)} in progress`,
    },
    {
      title: 'Payment Completion',
      value: formatNum(statsData?.paymentPercentage),
      subtitle: 'Completion ratio of Payments',
      icon: AccountBalanceWallet,
      color: '#00b4db', // Cyan/Light Blue
      type: '%',
      otherText: '',
    },
  ];

  return (
    <Box>
      <Grid2 container spacing={3} alignItems="stretch">
        {cardConfigs.map((card, index) => (
          // Adjusted Grid size so 4 cards fit perfectly on large screens (lg: 3 = 25% width)
          <Grid2 key={index} size={{ lg: 3, md: 6, sm: 6, xs: 12 }}>
            <StatCard
              title={card.title}
              value={card.value}
              subtitle={card.subtitle}
              icon={card.icon}
              color={card.color}
              type={card.type}
              otherText={card.otherText}
            />
          </Grid2>
        ))}
      </Grid2>
    </Box>
  );
}